import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormGrid, SelectField, Spicy } from '@crave/ui';
import { api } from '../api.ts';
import { HistoryLink } from '../components/HistoryLink.tsx';
import {
  MeasureIn,
  formatQty,
  useMeasureStyle,
  type MeasurableIngredient,
} from '../components/MeasureIn.tsx';
import { Notice, Status, Table, errorText, money, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';
import { dateWords, statusWords } from './Orders.tsx';
import {
  STAFF_CANCEL_REASONS,
  explainCancellation,
  staffCancelReason,
  type StaffCancelCode,
} from '@crave/shared';

interface Detail {
  order: {
    id: string;
    orderNumber: string;
    status: string;
    /** The kitchen stage a CANCEL_REQUESTED order was at; declining returns it there. */
    resumeStatus: string | null;
    locationId: string;
    serviceDate: string;
    version: number;
    pickup_label: string;
    channel: string;
    /** A walk-up: "POS 4821 (Front iPad)" and who was signed in. */
    pos?: string | null;
    taken_by?: string | null;
    unitCount: number;
    subtotalCents: number;
    modifiersCents: number;
    discountCents: number;
    taxCents: number;
    tipCents: number;
    totalCents: number;
    amountDueCents: number;
    customerSnapshot: { name?: string; email?: string; phone?: string };
    notes: string | null;
    reviewReason: string | null;
    cancelReason: string | null;
  };
  items: {
    id: string;
    nameSnapshot: string;
    quantity: number;
    unitPriceCents: number;
    lineTotalCents: number;
    notes: string | null;
    spicy?: boolean;
    modifiers: { nameSnapshot: string; priceDeltaCents: number }[];
  }[];
  events: {
    seq: number;
    kind: string;
    fromStatus: string | null;
    toStatus: string | null;
    actorType: string;
    reason: string | null;
    payload: Record<string, unknown>;
    createdAt: string;
  }[];
  payment: {
    status: string;
    provider: string;
    amountCents: number;
    amountCapturedCents: number;
    amountRefundedCents: number;
    feeCents: number | null;
    captureBefore: string | null;
    lastErrorMessage: string | null;
  } | null;
  refunds: {
    amountCents: number;
    reason: string;
    status: string;
    note: string | null;
    createdAt: string;
  }[];
  comps: { amountCents: number; reason: string; note: string | null; createdAt: string }[];
  notifications: {
    template: string;
    channel: string;
    status: string;
    toAddress: string;
    sentAt: string | null;
    error: string | null;
  }[];
  inventory: {
    name: string;
    kind: string;
    reason: string;
    on_hand_delta: number;
    reserved_delta: number;
    created_at: string;
  }[];
  actions: {
    refundable_cents: number;
    can_cancel: boolean;
    in_review: boolean;
    /** A review order that was made, with the card still only authorized: it can be charged now. */
    can_capture: boolean;
  };
}

interface Override {
  to: string;
  label: string;
  /** Prompt text for the reason; defaults to "Reason for: <label>". */
  question?: string | undefined;
  /** Asked with confirm() before the reason, for actions that are easy to get wrong. */
  warning?: string | undefined;
}

// ------------------------------------------------------------------ plain words for codes

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const CHANNEL_WORDS: Record<string, string> = {
  ONLINE: 'Ordered online',
  STAFF: 'Entered by staff',
  EVENT: 'Event order',
  TASTING: 'Tasting order',
};

const PAYMENT_WORDS: Record<string, string> = {
  REQUIRES_PAYMENT_METHOD: 'waiting for a card',
  REQUIRES_ACTION: 'waiting for the customer to confirm',
  PROCESSING: 'processing',
  SETUP_SAVED: 'card saved for later',
  AUTHORIZED: 'authorized, not charged yet',
  CAPTURED: 'charged',
  CANCELED: 'canceled',
  FAILED: 'failed',
  AUTH_EXPIRED: 'authorization expired',
};

/** Refund and comp reasons (the codes the API takes) as the owner says them. */
const REASON_WORDS: Record<string, string> = {
  QUALITY: 'Food quality',
  GOODWILL: 'Goodwill',
  CUSTOMER_CANCEL: 'Customer canceled',
  SUBSTITUTION: 'An item was swapped',
  DUPLICATE: 'Charged twice',
  NO_SHOW_WAIVED: 'No-show forgiven',
  SERVICE_RECOVERY: 'Making up for a service problem',
  ORDER_PROBLEM: 'Problem with the order',
  FOOD_QUALITY: 'Food quality',
  MANAGER_GOODWILL: 'Manager goodwill',
  CUSTOMER_SHORT_PAYMENT: 'Customer was short on payment',
  EMPLOYEE_MEAL: 'Employee meal',
  MARKETING: 'Marketing',
  OWNER_AUTHORIZATION: 'Approved by the owner',
  OTHER: 'Other',
};

/** A reason code in words; typed reasons ("checkout", "Customer called") are kept. */
function reasonWords(reason: string | null | undefined): string {
  if (!reason) return '';
  if (REASON_WORDS[reason]) return REASON_WORDS[reason];
  if (/^[A-Z][A-Z0-9_]+$/.test(reason)) return statusWords(reason);
  return capitalize(reason);
}

const EVENT_WORDS: Record<string, string> = {
  NOTE: 'Note added',
  PAYMENT: 'Payment',
  NOTIFICATION: 'Message to the customer',
  SUBSTITUTION: 'Item swapped',
  COMP: 'Comp',
  REFUND: 'Refund',
  INVENTORY: 'Inventory',
  TIP: 'Tip',
};

const ACTOR_WORDS: Record<string, string> = {
  CUSTOMER: 'Customer',
  STAFF: 'Staff',
  SYSTEM: 'Automatic',
  WEBHOOK: 'Payment service',
};

const REFUND_STATUS_WORDS: Record<string, string> = {
  pending: 'waiting',
  succeeded: 'done',
  failed: 'failed',
  canceled: 'canceled',
};

/** Email templates (apps/api/src/listeners/notifications.ts) by what the customer was told. */
const TEMPLATE_WORDS: Record<string, string> = {
  confirmed: 'Order confirmed',
  scheduled: 'Order scheduled',
  preparing: 'Being made',
  in_oven: 'In the oven',
  ready: 'Ready for pickup',
  receipt: 'Receipt',
  canceled: 'Order canceled',
  payment_failed: 'Payment did not go through',
};

const NOTIFICATION_STATUS_WORDS: Record<string, string> = {
  queued: 'Waiting to send',
  sent: 'Sent',
  failed: 'Did not send',
  skipped: 'Not needed',
};

const INVENTORY_KIND_WORDS: Record<string, string> = {
  RECEIVE: 'Received',
  RESERVE: 'Set aside for this order',
  RELEASE: 'No longer set aside',
  CONSUME: 'Used',
  PRODUCE: 'Made',
  ADJUST: 'Adjusted',
  TRANSFER: 'Moved',
};

const INVENTORY_REASON_WORDS: Record<string, string> = {
  PURCHASE: 'purchase',
  RECIPE: 'for the recipe',
  WASTE: 'waste',
  TESTING: 'testing',
  SAMPLING: 'tasting or sampling',
  PRODUCTION: 'production',
  SPILLAGE: 'spillage',
  EMPLOYEE_MEAL: 'employee meal',
  EVENT: 'event',
  MANUAL_CORRECTION: 'count correction',
  RESERVATION: '',
  REMAKE: 'remake',
  PHYSICAL_COUNT: 'physical count',
};

function inventoryWords(kind: string, reason: string): string {
  const k = INVENTORY_KIND_WORDS[kind] ?? statusWords(kind);
  const r = INVENTORY_REASON_WORDS[reason] ?? statusWords(reason).toLowerCase();
  return r ? `${k} (${r})` : k;
}

/** Kitchen stages. The database does not allow canceling from these; hold for review first. */
const STAGE_NAME: Record<string, string> = {
  PREPARING: 'prep',
  IN_OVEN: 'the oven',
  BOXING: 'boxing',
  READY: 'ready',
};
const IN_KITCHEN = Object.keys(STAGE_NAME);

/** Comps are only possible before pickup, on an order that was paid for. */
const COMP_CLOSED = [
  'COMPLETED',
  'CANCELED',
  'NO_SHOW',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'PICKED_UP',
  'DRAFT',
  'AWAITING_PAYMENT',
  'PAYMENT_FAILED',
];

/** Staff transitions offered for the order's current status (mirrors order_transitions). */
function overridesFor(
  o: Detail['order'],
  payment: Detail['payment'],
  events: Detail['events'],
  canCapture: boolean,
) {
  const onlyAuthorized = payment?.status === 'AUTHORIZED';
  const hold: Override = {
    to: 'MANUAL_REVIEW',
    label: 'Hold for manager review',
    question:
      "This pizza is already being made, so it can't be canceled directly. Why hold it for review?",
  };
  switch (o.status) {
    case 'MANUAL_REVIEW': {
      const reachedKitchen = events.some(
        (e) =>
          e.toStatus !== null && (IN_KITCHEN.includes(e.toStatus) || e.toStatus === 'PICKED_UP'),
      );
      // Once it was made, Confirmed or Queue would send it through the kitchen again and use up
      // ingredients twice, so only READY is offered. A READY order past its pickup time becomes a
      // no-show, so with an authorized card "Charge card now" is the better way to collect.
      const list: Override[] = reachedKitchen
        ? [
            {
              to: 'READY',
              label: onlyAuthorized ? 'Back to ready (charge at pickup)' : 'Back to ready',
              question: onlyAuthorized
                ? 'Why send it back to ready? The card is charged when it is marked picked up. Past the pickup time it may become a no-show.'
                : 'Why send it back to ready? Past the pickup time it may become a no-show.',
            },
          ]
        : [
            { to: 'CONFIRMED', label: 'Resolve → Confirmed' },
            { to: 'QUEUED', label: 'Resolve → Queue' },
          ];
      list.push(
        {
          to: 'COMPLETED',
          label: 'Resolve → Completed',
          warning: onlyAuthorized
            ? `The card is only authorized, not charged. Completing now means this order is never paid.${canCapture ? ' To collect, use "Charge card now" instead.' : ''} Complete anyway?`
            : undefined,
        },
        { to: 'CANCELED', label: 'Resolve → Canceled' },
      );
      return list;
    }
    case 'CANCEL_REQUESTED': {
      const resume = o.resumeStatus && STAGE_NAME[o.resumeStatus] ? o.resumeStatus : 'PREPARING';
      return [
        { to: 'CANCELED', label: 'Approve cancellation' },
        {
          to: resume,
          label: `Decline, back to ${STAGE_NAME[resume]}`,
          question: `Why decline? The order goes back to ${STAGE_NAME[resume]}.`,
        },
      ];
    }
    case 'PREPARING':
    case 'IN_OVEN':
    case 'BOXING':
      return [hold];
    case 'READY':
      return [
        { to: 'PICKED_UP', label: 'Mark picked up (captures payment)' },
        { to: 'NO_SHOW', label: 'Mark no-show' },
        hold,
      ];
    default:
      return [];
  }
}

/** Asks for a reason until it is 3 to 300 characters (what the API accepts) or the user cancels. */
function askReason(question: string): string | null {
  let hint = '';
  let last = '';
  for (;;) {
    const text = prompt(hint ? `${question}\n\n${hint}` : question, last);
    if (text === null) return null;
    const t = text.trim();
    last = t;
    if (t.length < 3) hint = 'Use at least 3 characters.';
    else if (t.length > 300) hint = 'Keep it to 300 characters or fewer.';
    else return t;
  }
}

export function OrderDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [canceling, setCanceling] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['order', id],
    queryFn: async () => (await api.get<Detail>(`/admin/orders/${id}`)).data,
    refetchInterval: 10_000,
  });
  const compOpen = q.data ? !COMP_CLOSED.includes(q.data.order.status) : false;
  // This order's comp budget, asked for by location and month so the answer does not depend on how
  // many budgets exist. If this fails (for example no comps.use), the form still works and the
  // API's answer is shown on submit.
  const compLocation = q.data?.order.locationId ?? '';
  const compMonth = q.data ? `${q.data.order.serviceDate.slice(0, 7)}-01` : '';
  const budgets = useQuery({
    queryKey: ['comp-budgets', compLocation, compMonth],
    queryFn: async () =>
      (
        await api.get<{ items: { location_id: string; month: string }[] }>(
          `/admin/reports/comps?location_id=${compLocation}&month=${compMonth}`,
        )
      ).data.items,
    enabled: compOpen,
    retry: false,
  });
  const [msg, setMsg] = useState<{
    kind: 'ok' | 'error';
    text: string;
    toReports?: boolean;
  } | null>(null);
  const done = (text: string) => {
    setMsg({ kind: 'ok', text });
    void qc.invalidateQueries({ queryKey: ['order', id] });
  };
  const fail = (e: unknown) => setMsg({ kind: 'error', text: errorText(e) });
  const transition = useMutation({
    mutationFn: (v: { to: string; reason: string }) =>
      api.post(`/admin/orders/${id}/transition`, { ...v, expected_version: q.data?.order.version }),
    onSuccess: () => done('Updated.'),
    onError: fail,
  });
  const refund = useMutation({
    mutationFn: (v: { amount_cents: number; reason: string; note?: string | undefined }) =>
      api.post(`/admin/orders/${id}/refund`, v),
    onSuccess: () => done('Refund issued.'),
    onError: fail,
  });
  const comp = useMutation({
    mutationFn: (v: { amount_cents: number; reason: string; note?: string | undefined }) =>
      api.post(`/admin/orders/${id}/comp`, v),
    onSuccess: () => {
      done('Comp applied.');
      void qc.invalidateQueries({ queryKey: ['comp-budgets'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && /no comp budget/i.test(e.message)) {
        setMsg({
          kind: 'error',
          text: 'No comp budget is set for this month, so the comp was not applied.',
          toReports: true,
        });
        void qc.invalidateQueries({ queryKey: ['comp-budgets'] });
      } else fail(e);
    },
  });
  const capture = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ status: string; payment_status: string | null; error: string | null }>(
          `/admin/orders/${id}/capture`,
        )
      ).data,
    onSuccess: (r) => {
      if (r.status === 'MANUAL_REVIEW') {
        // The capture ran and failed, so the order is back in review.
        setMsg({
          kind: 'error',
          text: r.error ? `The card was not charged: ${r.error}` : 'The card was not charged.',
        });
        void qc.invalidateQueries({ queryKey: ['order', id] });
      } else
        done(
          r.payment_status === 'CAPTURED'
            ? r.status === 'COMPLETED'
              ? 'Card charged. The order is complete.'
              : 'Card charged.'
            : r.payment_status === 'PROCESSING'
              ? 'The charge is processing. Check back in a few minutes.'
              : `Charge not confirmed yet (payment ${String(r.payment_status ?? 'unknown').toLowerCase()}).`,
        );
    },
    onError: (e) => {
      fail(e);
      void qc.invalidateQueries({ queryKey: ['order', id] });
    },
  });
  const note = useMutation({
    mutationFn: (v: { note: string }) => api.post(`/admin/orders/${id}/note`, v),
    onSuccess: () => done('Note added.'),
    onError: fail,
  });

  if (q.isError) return <Notice kind="error">Could not load this order.</Notice>;
  if (!q.data) return <p>Loading…</p>;
  const {
    order: o,
    items,
    events,
    payment,
    refunds,
    comps,
    notifications,
    inventory,
    actions,
  } = q.data;
  const showCharge = o.status === 'MANUAL_REVIEW' && actions.can_capture;
  const overrides = overridesFor(o, payment, events, showCharge);
  const busy = transition.isPending || capture.isPending;
  const inKitchen = IN_KITCHEN.includes(o.status);
  const showCancel =
    actions.can_cancel && !inKitchen && !overrides.some((x) => x.to === 'CANCELED');
  const noBudget =
    budgets.isSuccess &&
    !budgets.data.some(
      (b) =>
        b.location_id === o.locationId && String(b.month).slice(0, 7) === o.serviceDate.slice(0, 7),
    );
  const run = (a: Override) => {
    if (a.warning && !confirm(a.warning)) return;
    // A cancellation reason reaches the customer, so it is picked from plain choices, not typed.
    if (a.to === 'CANCELED') {
      setCanceling(a.label);
      return;
    }
    const reason = askReason(a.question ?? `Reason for: ${a.label}`);
    if (reason) transition.mutate({ to: a.to, reason });
  };
  const toReports = (
    <>
      {' '}
      The owner sets it in <Link to="/reports">Reports, under Comps</Link>.
    </>
  );

  return (
    // minmax(0, 1fr): wide tables scroll inside their cards instead of widening the page.
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <Link to="/orders" style={{ color: 'var(--ink-soft)' }}>
            ← Orders
          </Link>
          <h2 style={{ margin: '4px 0 0' }}>
            {o.orderNumber} <Status s={o.status} />
          </h2>
          <HistoryLink
            entityType="orders"
            entityId={o.id}
            name={`order ${o.orderNumber}`}
            label="History of this order"
            style={{ marginTop: 2 }}
          />
        </div>
        <div style={{ textAlign: 'right', marginLeft: 'auto' }}>
          <div style={{ fontWeight: 700 }}>
            Pickup {o.pickup_label.replace(/^\d{4}-\d{2}-\d{2}/, (d) => `${dateWords(d)},`)}
          </div>
          <div style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
            {o.unitCount} {o.unitCount === 1 ? 'pizza' : 'pizzas'} ·{' '}
            {CHANNEL_WORDS[o.channel] ?? statusWords(o.channel)}
            {o.pos && (
              <div>
                Taken on {o.pos}
                {o.taken_by ? ` by ${o.taken_by}` : ''}
              </div>
            )}
          </div>
        </div>
      </div>
      {msg && (
        <Notice kind={msg.kind}>
          {msg.text}
          {msg.toReports && toReports}
        </Notice>
      )}
      {o.reviewReason && <Notice kind="error">Manual review: {o.reviewReason}</Notice>}
      {o.status === 'MANUAL_REVIEW' && payment?.status === 'AUTHORIZED' && (
        <Notice>
          The card is authorized but not charged yet.
          {showCharge ? ' Use Charge card now to collect.' : ''}
        </Notice>
      )}
      {o.cancelReason && <Notice>Cancel reason: {o.cancelReason}</Notice>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: 16,
        }}
      >
        <Card>
          <strong>Customer</strong>
          <div>
            {o.customerSnapshot.name ?? (
              <span style={{ color: 'var(--ink-soft)' }}>No name given</span>
            )}
          </div>
          <div style={{ color: 'var(--ink-soft)', fontSize: 14 }}>
            {o.customerSnapshot.email}{' '}
            {o.customerSnapshot.phone ? `· ${o.customerSnapshot.phone}` : ''}
          </div>
          {o.notes && <div style={{ marginTop: 8 }}>“{o.notes}”</div>}
        </Card>
        <Card>
          <strong>Payment</strong>
          {payment ? (
            <div style={{ fontSize: 14 }}>
              <div>
                {capitalize(payment.provider)}:{' '}
                {PAYMENT_WORDS[payment.status] ?? statusWords(payment.status)}
              </div>
              <div>
                Authorized {money(payment.amountCents)} · captured{' '}
                {money(payment.amountCapturedCents)} · refunded {money(payment.amountRefundedCents)}
                {payment.feeCents !== null ? ` · fee ${money(payment.feeCents)}` : ''}
              </div>
              {payment.captureBefore && (
                <div style={{ color: 'var(--ink-soft)' }}>
                  Authorization valid until {when(payment.captureBefore)}
                </div>
              )}
              {payment.lastErrorMessage && (
                <div style={{ color: 'var(--crave-red)' }}>{payment.lastErrorMessage}</div>
              )}
            </div>
          ) : (
            <div style={{ color: 'var(--ink-soft)' }}>No payment yet.</div>
          )}
        </Card>
      </div>

      <Card>
        {items.map((i) => (
          <div
            key={i.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '6px 0',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <span>
              {i.quantity} × {i.nameSnapshot}
              {i.spicy ? (
                <>
                  {' '}
                  <Spicy size="sm" />
                </>
              ) : null}
              {i.modifiers.length ? (
                <span style={{ color: 'var(--ink-soft)' }}>
                  {' '}
                  · {i.modifiers.map((m) => m.nameSnapshot).join(', ')}
                </span>
              ) : null}
              {i.notes ? (
                <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>“{i.notes}”</div>
              ) : null}
            </span>
            <span>{money(i.lineTotalCents)}</span>
          </div>
        ))}
        <div style={{ display: 'grid', gap: 2, marginTop: 8, fontSize: 14 }}>
          <Row l="Food" v={money(o.subtotalCents + o.modifiersCents)} />
          <Row l="Comps / discounts" v={`−${money(o.discountCents)}`} />
          <Row l="Tax" v={money(o.taxCents)} />
          <Row l="Tip" v={money(o.tipCents)} />
          <Row l="Amount due" v={money(o.amountDueCents)} bold />
        </div>
      </Card>

      <Card>
        <strong>Actions</strong>
        <style>{`@media (max-width: 560px) { .od-actions > * { flex: 1 1 100%; } }`}</style>
        <div
          className="od-actions"
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}
        >
          {showCharge && (
            <Button
              onClick={() => {
                if (
                  confirm(
                    `Charge ${money(o.amountDueCents)} to the card now and mark the order picked up?`,
                  )
                )
                  capture.mutate();
              }}
              disabled={busy}
            >
              {capture.isPending ? 'Charging…' : 'Charge card now'}
            </Button>
          )}
          {overrides.map((a) => (
            <Button key={a.to} variant="secondary" onClick={() => run(a)} disabled={busy}>
              {a.label}
            </Button>
          ))}
          {showCancel && (
            <Button
              variant="danger"
              onClick={() =>
                run({ to: 'CANCELED', label: 'Cancel order', question: 'Reason for canceling' })
              }
              disabled={busy}
            >
              Cancel order
            </Button>
          )}
        </div>
        {canceling && (
          <CancelDialog
            title={canceling}
            busy={busy}
            onClose={() => setCanceling(null)}
            onConfirm={(reason) => {
              transition.mutate({ to: 'CANCELED', reason });
              setCanceling(null);
            }}
          />
        )}
        {inKitchen && (
          <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--ink-soft)' }}>
            Already in the kitchen. To cancel, hold it for manager review, then cancel from there.
          </p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
          {actions.refundable_cents > 0 && (
            <MoneyForm
              title={`Refund (up to ${money(actions.refundable_cents)})`}
              reasons={[
                'QUALITY',
                'GOODWILL',
                'CUSTOMER_CANCEL',
                'SUBSTITUTION',
                'DUPLICATE',
                'NO_SHOW_WAIVED',
                'OTHER',
              ]}
              max={actions.refundable_cents}
              onSubmit={(v) => refund.mutate(v)}
              busy={refund.isPending}
            />
          )}
          {compOpen && (
            <MoneyForm
              title="Comp (before pickup)"
              notice={
                noBudget ? (
                  <span style={{ fontSize: 14, color: 'var(--crave-red)' }}>
                    No comp budget is set for this month yet, so comps will be refused.
                    {toReports}
                  </span>
                ) : undefined
              }
              reasons={[
                'SERVICE_RECOVERY',
                'ORDER_PROBLEM',
                'FOOD_QUALITY',
                'MANAGER_GOODWILL',
                'CUSTOMER_SHORT_PAYMENT',
                'EMPLOYEE_MEAL',
                'MARKETING',
                'OWNER_AUTHORIZATION',
                'OTHER',
              ]}
              max={o.amountDueCents}
              onSubmit={(v) => comp.mutate(v)}
              busy={comp.isPending}
            />
          )}
          <NoteForm onSubmit={(n) => note.mutate({ note: n })} busy={note.isPending} />
        </div>
      </Card>

      <Card>
        <strong>Timeline</strong>
        <Table
          head={['When', 'Event', 'By', 'Reason']}
          wrapColumns={[1, 3]}
          rows={events.map((e) => [
            when(e.createdAt),
            e.kind === 'STATUS_CHANGE'
              ? e.fromStatus
                ? `${statusWords(e.fromStatus)} → ${statusWords(e.toStatus)}`
                : e.toStatus === 'DRAFT'
                  ? 'Order started'
                  : `Order started: ${statusWords(e.toStatus).toLowerCase()}`
              : `${EVENT_WORDS[e.kind] ?? statusWords(e.kind)}${e.payload && 'amount_cents' in e.payload ? ` ${money(Number(e.payload['amount_cents']))}` : ''}`,
            ACTOR_WORDS[e.actorType] ?? statusWords(e.actorType),
            reasonWords(e.reason),
          ])}
        />
      </Card>
      {(refunds.length > 0 || comps.length > 0) && (
        <Card>
          <strong>Money back</strong>
          <Table
            head={['When', 'Type', 'Amount', 'Reason', 'Note']}
            wrapColumns={[3, 4]}
            rows={[
              ...refunds.map((r) => [
                when(r.createdAt),
                `Refund (${REFUND_STATUS_WORDS[r.status] ?? r.status})`,
                money(r.amountCents),
                reasonWords(r.reason),
                r.note ?? '',
              ]),
              ...comps.map((c) => [
                when(c.createdAt),
                'Comp',
                money(c.amountCents),
                reasonWords(c.reason),
                c.note ?? '',
              ]),
            ]}
          />
        </Card>
      )}
      <Card>
        <strong>Notifications</strong>
        <Table
          head={['Message', 'To', 'Status', 'Sent']}
          wrapColumns={[2]}
          rows={notifications.map((n) => [
            TEMPLATE_WORDS[n.template] ?? capitalize(n.template.replace(/_/g, ' ')),
            n.toAddress,
            (NOTIFICATION_STATUS_WORDS[n.status] ?? capitalize(n.status)) +
              (n.error ? `: ${n.error}` : ''),
            n.sentAt ? when(n.sentAt) : 'Not sent yet',
          ])}
          empty="None sent yet."
        />
      </Card>
      <InventoryCard rows={inventory} />
    </div>
  );
}

/** Stock this order set aside or used, in the units the owner picked. */
function InventoryCard({ rows }: { rows: Detail['inventory'] }) {
  const canSeeUnits = useStaff().can('recipes.manage');
  const [style] = useMeasureStyle();
  // The order's rows carry only the ingredient name, so the units come from the ingredient list.
  const ingredients = useQuery({
    queryKey: ['order-detail-ingredient-units'],
    queryFn: async () =>
      (
        await api.get<{ items: (MeasurableIngredient & { name: string })[] }>(
          '/admin/recipes/ingredients',
        )
      ).data.items,
    enabled: canSeeUnits && rows.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const byName = new Map((ingredients.data ?? []).map((i) => [i.name, i]));
  const amount = (n: number, name: string) => {
    if (!n) return '0';
    const text = formatQty(n, byName.get(name) ?? null, style);
    return n > 0 ? `+${text}` : text.replace(/^-/, '−');
  };
  return (
    <Card>
      <div className="od-inventory-head">
        <strong>Inventory</strong>
        {rows.length > 0 && canSeeUnits && <MeasureIn label="Show amounts in" />}
      </div>
      <style>{`.od-inventory-head { display: grid; gap: 8px; margin-bottom: 4px; }`}</style>
      <Table
        head={['When', 'Ingredient', 'What happened', 'Change on hand', 'Change set aside']}
        rows={rows.map((t) => [
          when(t.created_at),
          t.name,
          inventoryWords(t.kind, t.reason),
          amount(t.on_hand_delta, t.name),
          amount(t.reserved_delta, t.name),
        ])}
        empty="No inventory movements."
      />
    </Card>
  );
}

function Row({ l, v, bold }: { l: string; v: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: bold ? 700 : 400 }}>
      <span>{l}</span>
      <span>{v}</span>
    </div>
  );
}

/** Each form in the Actions card is its own full-width block, separated by a rule. */
const formBlock = {
  display: 'grid',
  gap: 8,
  marginTop: 16,
  paddingTop: 16,
  borderTop: '1px solid var(--line)',
} as const;

function MoneyForm({
  title,
  notice,
  reasons,
  max,
  onSubmit,
  busy,
}: {
  title: string;
  notice?: ReactNode;
  reasons: string[];
  max: number;
  onSubmit: (v: { amount_cents: number; reason: string; note?: string | undefined }) => void;
  busy: boolean;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState(reasons[0]!);
  const [note, setNote] = useState('');
  const cents = Math.round(Number(amount) * 100);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (cents > 0 && cents <= max)
          onSubmit({ amount_cents: cents, reason, note: note.trim() || undefined });
      }}
      style={formBlock}
    >
      <strong>{title}</strong>
      {notice}
      <FormGrid min={240}>
        <Field
          label="Amount ($)"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <SelectField
          label="Reason"
          options={reasons.map((r) => [r, reasonWords(r)] as const)}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Field
          label="Note (optional)"
          wide
          value={note}
          maxLength={300}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormGrid>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button type="submit" variant="secondary" disabled={busy || !(cents > 0 && cents <= max)}>
          Apply
        </Button>
      </div>
    </form>
  );
}

function NoteForm({ onSubmit, busy }: { onSubmit: (n: string) => void; busy: boolean }) {
  const [n, setN] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (n.trim()) {
          onSubmit(n.trim());
          setN('');
        }
      }}
      style={formBlock}
    >
      <strong>Add a note</strong>
      <FormGrid min={240}>
        <Field label="Note" wide value={n} maxLength={500} onChange={(e) => setN(e.target.value)} />
      </FormGrid>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button type="submit" variant="secondary" disabled={busy || !n.trim()}>
          Save note
        </Button>
      </div>
    </form>
  );
}

/**
 * Canceling tells the customer why, by text and email, and on their tracking page. So the reason is
 * one of a few plain choices, with an optional note written for the customer to read.
 */
function CancelDialog({
  title,
  busy,
  onClose,
  onConfirm,
}: {
  title: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [code, setCode] = useState<StaffCancelCode | ''>('');
  const [note, setNote] = useState('');
  const preview = code
    ? explainCancellation({
        reason: staffCancelReason(code, note),
        actor: 'STAFF',
        authorized: false,
        creditReturnedCents: 0,
      })
    : null;
  return (
    <div
      role="dialog"
      aria-label={title}
      style={{
        marginTop: 12,
        padding: 16,
        borderRadius: 16,
        background: 'var(--paper)',
        boxShadow: '0 0 0 1px var(--line), 0 12px 32px rgba(20,20,20,.08)',
        display: 'grid',
        gap: 12,
      }}
    >
      <strong style={{ fontSize: 17 }}>{title}</strong>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
        The customer gets a text and an email saying why, what happens to their money, and what to
        do next. Pick the reason they will read.
      </p>
      <div style={{ display: 'grid', gap: 6 }}>
        {STAFF_CANCEL_REASONS.map((r) => (
          <label
            key={r.code}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minHeight: 40,
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="cancel-reason"
              checked={code === r.code}
              onChange={() => setCode(r.code)}
              style={{ width: 20, height: 20 }}
            />
            {r.label}
          </label>
        ))}
      </div>
      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontWeight: 600 }}>Note to the customer (optional)</span>
        <textarea
          value={note}
          maxLength={250}
          rows={2}
          onChange={(e) => setNote(e.target.value)}
          placeholder="For example: We sold out of fresh mozzarella tonight."
          style={{
            font: 'inherit',
            padding: 10,
            borderRadius: 10,
            border: '1px solid var(--line)',
          }}
        />
      </label>
      {preview && (
        <div style={{ fontSize: 14, background: 'var(--mist)', borderRadius: 12, padding: 12 }}>
          <strong>The customer reads:</strong> {preview.why}
          {preview.staffNote ? ` ${preview.staffNote}` : ''} {preview.next}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Button
          variant="danger"
          disabled={!code || busy}
          onClick={() => code && onConfirm(staffCancelReason(code, note))}
        >
          Cancel order and tell the customer
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Keep order
        </Button>
      </div>
    </div>
  );
}
